<script lang="ts" setup>
import UIcon from '@d/form/components/UIcon.vue'
import { mdiClose } from '@mdi/js'
import { useVModel } from '@vueuse/core'

interface UDialogProps {
  modelValue?: boolean
  persistent?: boolean
  fullscreen?: boolean
  title?: string
  color?: string
  closable?: boolean
  closeIcon?: string
  titleIcon?: string
  smallTitle?: boolean
  maxWidth?: string | number
  scrollable?: boolean
  changed?: boolean
  disableAnimations?: boolean
}

const props = withDefaults(defineProps<UDialogProps>(), {
  modelValue: false,
  smallTitle: false,
  title: undefined,
  color: 'primary',
  persistent: false,
  fullscreen: false,
  closable: true,
  titleIcon: undefined,
  closeIcon: undefined,
  maxWidth: undefined,
  scrollable: undefined,
  changed: false,
  disableAnimations: false,
})
const emits = defineEmits(['update:modelValue', 'close'])

const slots = useSlots()

const shown = useVModel(props, 'modelValue', emits)
const shownInternalStorage = ref(shown.value)
const shownInternal = computed({
  get: () => shownInternalStorage.value,
  set: (v) => { shown.value = v },
})

watch(shown, () => {
  if (shown.value) {
    shownInternalStorage.value = true
  }
  if (!shown.value && !props.changed) {
    shownInternalStorage.value = false
    emits('close')
  }
})
</script>

<template>
  <v-dialog
    v-model="shownInternal" class="u-dialog" :persistent="persistent" :max-width="maxWidth"
    :fullscreen="fullscreen"
    :transition="disableAnimations ? 'none' : undefined"
  >
    <v-card class="u-dialog-card">
      <v-toolbar
        class="flex-grow-0" :color="color ?? 'surface'"
        :density="title ? undefined : 'compact'"
      >
        <template #prepend>
          <div v-if="smallTitle" style="position: relative">
            <div class="u-background-title">
              {{ title }}
            </div>
          </div>
        </template>
        <slot name="toolbar.title">
          <v-toolbar-title v-if="!smallTitle">
            <div v-if="title || titleIcon">
              <span class="d-flex justify-start align-center">
                <UIcon v-if="titleIcon" class="mr-2" :icon="titleIcon" />
                {{ title }}
              </span>
            </div>
            <div v-else>
              <slot name="title" />
            </div>
          </v-toolbar-title>
        </slot>

        <v-toolbar-items>
          <v-btn v-if="closable" icon :disabled="persistent" @click="shown = false">
            <UIcon :icon="closeIcon ?? mdiClose" />
          </v-btn>
        </v-toolbar-items>
        <template v-if="slots.toolbar" #extension>
          <slot name="toolbar" />
        </template>
      </v-toolbar>

      <div class="u-dialog-wrapper" :class="{ fullscreen }">
        <slot />
      </div>

      <v-divider v-if="slots.actions" />
      <v-sheet v-if="slots.actions" class="d-flex u-dialog-actions">
        <slot name="actions" />
      </v-sheet>
    </v-card>
  </v-dialog>
</template>

<style scoped>
.u-dialog-card {
    overflow: hidden !important;
}

.u-dialog-wrapper,
.u-dialog-card :deep(.v-card-text) {
    overflow: auto !important;
}

.u-background-title {
    color: rgba(var(--v-theme-on-primary), 0.5);
    position: absolute;
    left: 0;
    bottom: 0.3rem;
    z-index: 1;
    user-select: none;
    font-size: 1rem;
    font-weight: bold;
}

.fullscreen {
  height: 100%
}

/* .u-dialog-actions {
    background: rgba(var(--v-theme-on-surface), 0.05);
  } */
</style>
